import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, Invitation } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';

const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.getInvitation(token)
      .then(setInvitation)
      .catch(err => setError(err instanceof Error ? err.message : 'Invitation not found or expired'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    try {
      setAccepting(true);
      await api.acceptInvitation(token);
      showSnackbar('Invitation accepted.', { variant: 'success' });
      navigate(`/questions/${invitation!.question_id}`);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to accept invitation', { variant: 'error' });
      setAccepting(false);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;

  if (error) {
    return (
      <div className="invitation-page container">
        <div className="invitation-card">
          <h2>Invalid invitation</h2>
          <p className="invitation-sub">{error}</p>
          <Link to="/" className="btn btn-secondary btn-sm">Go home</Link>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  const isAccepted = !!invitation.accepted_at;

  return (
    <div className="invitation-page container">
      <div className="invitation-card">
        <h2>You&rsquo;ve been invited to collaborate on</h2>
        <p className="invitation-question-title">"{invitation.question_title}"</p>

        {isAccepted ? (
          <>
            <p className="invitation-sub">This invitation has already been accepted.</p>
            {isAuthenticated && (
              <Link to={`/questions/${invitation.question_id}`} className="btn btn-primary">
                Open question
              </Link>
            )}
          </>
        ) : isAuthenticated ? (
          <>
            <p className="invitation-sub">Accept to start contributing data to this question.</p>
            <button
              onClick={handleAccept}
              className="btn btn-primary"
              disabled={accepting}
            >
              {accepting ? 'Accepting…' : 'Accept invitation'}
            </button>
          </>
        ) : (
          <>
            <p className="invitation-sub">Log in or create an account to accept this invitation.</p>
            <div className="invitation-auth-actions">
              <Link
                to={`/login?redirect=/invitations/${token}`}
                className="btn btn-primary"
              >
                Log in
              </Link>
              <Link
                to={`/signup?redirect=/invitations/${token}`}
                className="btn btn-secondary"
              >
                Sign up
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvitationPage;
